import { httpService } from "../services/httpService";

/** Frontend step id → backend setup key */
export const STEP_KEY_MAP = {
  businessHours: "business_hours",
  client: "client_pet",
  service: "service",
  onlineProfile: "online_profile",
  booking: "booking",
  product: "product",
  posSale: "pos_sale",
  staff: "staff",
  room: "room",
  supplier: "supplier",
  purchaseBill: "purchase_bill",
  businessKyc: "business_kyc",
};

const normalizeBackendKey = (key) => (key ? String(key).trim().toLowerCase() : "");

export const REVERSE_KEY_MAP = Object.fromEntries(
  Object.entries(STEP_KEY_MAP).map(([frontend, backend]) => [
    normalizeBackendKey(backend),
    frontend,
  ])
);

export const normalizeCompletionValue = (value) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    const v = value.trim().toLowerCase();
    if (["true", "1", "yes", "y", "completed", "done"].includes(v)) return true;
    if (["false", "0", "no", "n", "pending", "not_completed", "incomplete"].includes(v)) {
      return false;
    }
  }
  return Boolean(value);
};

/** Returns frontend step ids that are completed according to the DB response. */
export const extractCompletedStepIds = (setupData) => {
  const rawSteps = Array.isArray(setupData)
    ? setupData
    : setupData?.steps ?? setupData?.setup_steps ?? setupData?.data ?? [];

  if (!Array.isArray(rawSteps)) return [];

  return rawSteps
    .filter((s) => {
      const isDone = normalizeCompletionValue(
        s?.completed ?? s?.is_completed ?? s?.isCompleted ?? s?.status
      );
      return isDone === true;
    })
    .map((s) => {
      const key = s?.key ?? s?.step_key ?? s?.backend_key ?? s?.slug ?? s?.step;
      const normalizedKey = normalizeBackendKey(key);
      return REVERSE_KEY_MAP[normalizedKey] || null;
    })
    .filter(Boolean);
};

export async function fetchVendorSetup(vendorId) {
  const response = await httpService.get(`/vendorregistration/getsetup/${vendorId}`);
  return response.data ?? null;
}

/** Persist a single setup step as completed in the database. */
export async function markSetupStepComplete(vendorId, backendKey) {
  if (!vendorId || !backendKey) return null;

  const payload = {
    steps: [{ key: backendKey, completed: true }],
  };

  const response = await httpService.put(
    `/vendorregistration/updatesetup/${vendorId}`,
    payload
  );
  return response.data ?? null;
}

/** Mark complete using either a frontend step id or a backend key. */
export async function markSetupStepCompleteById(vendorId, stepIdOrKey) {
  const backendKey = STEP_KEY_MAP[stepIdOrKey] || stepIdOrKey;
  return markSetupStepComplete(vendorId, backendKey);
}

/** Infer whether the vendor chose to go online (yes) or skip (no). */
export function inferTakeOnlineChoice(profileData, completedStepIds = []) {
  if (!completedStepIds.includes("onlineProfile")) return null;

  const hasOnlineSetup = Boolean(
    profileData?.business_name ||
      profileData?.company_name ||
      profileData?.address?.length ||
      profileData?.profile_image ||
      profileData?.logo ||
      profileData?.is_online_booking_enabled ||
      profileData?.online_booking_enabled
  );

  return hasOnlineSetup ? true : false;
}

// ---------------------------------------------------------------------------
// computeRequiredStepIds
// ---------------------------------------------------------------------------
/**
 * Given a plan's category array (plan.plan_category) plus the vendor's chosen
 * optional and primary category IDs, returns the list of **required**
 * (non-optional, non-meta) Getting Started step IDs that would be generated
 * for that plan.
 *
 * This mirrors the `allSteps` useMemo in GettingStarted.jsx but is a plain
 * function so it can be called outside of React (e.g. inside onPlanChange).
 *
 * @param {Array}       planCategories      - plan.plan_category array
 * @param {Array}       selectedOptionalIds - optional category IDs the vendor chose
 * @param {string|null} selectedPrimaryId   - the primary/mandatory category ID chosen
 * @returns {string[]} required step IDs
 */
export function computeRequiredStepIds(
  planCategories = [],
  selectedOptionalIds = [],
  selectedPrimaryId = null,
) {
  if (!planCategories.length) return [];

  const normalizeName = (cat) =>
    String(
      cat.service_category_name || cat.category_name || cat.name || "",
    ).toLowerCase();

  // ── Determine POS presence ──────────────────────────────────────────────
  const hasPOS = planCategories.some((cat) => {
    const name = normalizeName(cat);
    return name.includes("pet shop") || name.includes("pos");
  });

  const nonPOSCategories = planCategories.filter((cat) => {
    const name = normalizeName(cat);
    return !name.includes("pet shop") && !name.includes("pos");
  });

  const isPOSOnly = hasPOS && nonPOSCategories.length === 0;

  // ── Determine optional POS (POS exists but is not the sole category) ───
  const posCategory = planCategories.find((cat) => {
    const name = normalizeName(cat);
    return name.includes("pet shop") || name.includes("pos");
  });
  const posId = posCategory?.service_category_id;
  const hasOptionalPOS =
    hasPOS &&
    !isPOSOnly &&
    (selectedOptionalIds.includes(posId) ||
      selectedOptionalIds.includes(String(posId)));

  // ── Resolve the main (mandatory) category ──────────────────────────────
  let mainCategory;
  if (selectedPrimaryId) {
    mainCategory = planCategories.find(
      (cat) => String(cat.service_category_id) === String(selectedPrimaryId),
    );
  }
  if (!mainCategory) {
    mainCategory =
      planCategories.find((cat) => cat.is_optional === 0) ||
      planCategories[0];
  }

  const mainType = (() => {
    if (!mainCategory) return "none";
    const name = normalizeName(mainCategory);
    if (name.includes("grooming")) return "grooming";
    if (name.includes("boarding") || name.includes("kennel")) return "boarding";
    if (name.includes("vet") || name.includes("clinic")) return "vet";
    if (name.includes("pet shop") || name.includes("pos")) return "pos";
    return "other";
  })();

  // ── Build required step ids ─────────────────────────────────────────────
  const steps = [];

  if (isPOSOnly) {
    // POS-only plan: product → supplier → purchaseBill → posSale (staff is optional)
    steps.push("product", "supplier", "purchaseBill", "posSale");
  } else {
    // Business Hours — Grooming and Vet only
    if (mainType === "grooming" || mainType === "vet") {
      steps.push("businessHours");
    }

    // Client & Pet — always for non-POS plans
    steps.push("client");

    // Room — Boarding only
    if (mainType === "boarding") {
      steps.push("room");
    }

    // Service — Grooming, Vet, Boarding
    if (mainType !== "none" && mainType !== "other" && mainType !== "pos") {
      steps.push("service");
    }

    // Booking — non-POS
    if (mainType !== "none" && mainType !== "other" && mainType !== "pos") {
      steps.push("booking");
    }

    // Optional POS steps — only when POS is an optional add-on the vendor selected
    if (hasOptionalPOS) {
      steps.push("product", "posSale");
    }

    // Business KYC — always required for non-POS plans
    steps.push("businessKyc");

    // NOTE: takeOnline, onlineProfile, and staff are intentionally excluded —
    //       they are either optional, special-flow, or do not block completion.
  }

  return steps;
}
